import { gql } from "@apollo/client";
import FetchMoreButton from "atoms/FetchMoreButton";
import Grid from "atoms/Grid";
import Stack from "atoms/Stack";
import { PageContentStack } from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_GREY_BORDER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import useSearchTerm from "helpers/useSearchTerm";
import React, { useMemo } from "react";
import PageTitle from "shared/PageTitle";

import SchickBanner from "../molecules/SchickBanner";
import GameEntry, { GameEntryFragment } from "./games/GameEntry";
import GamesBlankMessage from "./games/GamesBlankMessage";
import GamesFilterBar from "./games/GamesFilterBar";

export default function GamesPage() {
  useEnforceAuth({ enforceAuthenticated: true });

  const [teamId, teamIdSet] = useRouteState("teamId");
  const [filterYear, filterYearSet] = useRouteState("gamesYear");
  const [filterLeagueId, filterLeagueIdSet] = useRouteState("gamesLeague");
  const [filterProfileId, filterProfileIdSet] = useRouteState("gamesProfile");

  const [keyword, keywordSet, keywordDebounced] = useSearchTerm();

  const filterQueryVariables = useMemo(
    () => ({
      teamId,
      year: filterYear,
      leagueId: filterLeagueId,
      profileId: filterProfileId,
    }),
    [teamId, filterYear, filterLeagueId, filterProfileId],
  );

  const [filtersData] = useData(
    gql`
      query GamesPage($teamId: ID, $year: String, $leagueId: ID, $profileId: ID) {
        currentUser {
          id
          gamesFilterOptions(teamId: $teamId, year: $year, leagueId: $leagueId, profileId: $profileId) {
            years
            leagues {
              id
              name
            }
            teams {
              id
              name
            }
            profiles {
              id
              name
              # display org name in profile filter options
              org {
                id
                name
              }
            }
          }
        }
      }
    `,
    filterQueryVariables,
  );

  const gamesSearchTerm = useMemo(() => {
    const trimmed = (keywordDebounced || "").trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [keywordDebounced]);

  const gamesQueryVariables = useMemo(
    () => ({
      teamId,
      year: filterYear,
      leagueId: filterLeagueId,
      profileId: filterProfileId,
      searchTerm: gamesSearchTerm,
    }),
    [teamId, filterYear, filterLeagueId, filterProfileId, gamesSearchTerm],
  );

  const [dataGames, dataGamesMeta] = useData(
    gql`
      query GamesPage_game(
        $teamId: ID
        $offset: Int
        $year: String
        $leagueId: ID
        $profileId: ID
        $searchTerm: String
      ) {
        currentUser {
          id
          gamesCount(teamId: $teamId, year: $year, leagueId: $leagueId, profileId: $profileId, searchTerm: $searchTerm)
          games(
            teamId: $teamId
            year: $year
            leagueId: $leagueId
            profileId: $profileId
            searchTerm: $searchTerm
            offset: $offset
            limit: 10
          ) {
            id
            ...GameEntryFragment
          }
        }
      }
      ${GameEntryFragment}
    `,
    gamesQueryVariables,
  );

  const filterOptions = filtersData?.currentUser?.gamesFilterOptions;
  const yearOptions = filterOptions?.years ?? [];
  const leagueOptions = filterOptions?.leagues ?? [];
  const teamOptions = filterOptions?.teams ?? [];
  const profileOptions = filterOptions?.profiles ?? [];

  const handleResetFilters = () => {
    keywordSet("");
    filterYearSet(null);
    filterLeagueIdSet(null);
    filterProfileIdSet(null);
    teamIdSet(null);
  };

  // Games search results
  const games = dataGames?.currentUser?.games ?? [];
  const hasGames = games.length > 0;
  const hasAppliedFilters = Boolean(teamId || filterYear || filterLeagueId || filterProfileId || gamesSearchTerm);
  const showBaseBlankState = !hasGames && !dataGamesMeta?.loading && !hasAppliedFilters;
  const showFilteredEmptyState = !hasGames && !dataGamesMeta?.loading && hasAppliedFilters;

  return (
    <>
      <PageTitle title="Games" />
      <div
        style={{
          backgroundColor: "white",
          borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <GamesFilterBar
          keyword={keyword}
          onKeywordChange={keywordSet}
          years={yearOptions}
          selectedYear={filterYear}
          onYearChange={filterYearSet}
          leagues={leagueOptions}
          selectedLeagueId={filterLeagueId}
          onLeagueChange={filterLeagueIdSet}
          teams={teamOptions}
          selectedTeamId={teamId}
          onTeamChange={teamIdSet}
          profiles={profileOptions}
          selectedProfileId={filterProfileId}
          onProfileChange={filterProfileIdSet}
          onReset={handleResetFilters}
        />
      </div>
      <PageContentStack>
        <SchickBanner
          style={{
            alignSelf: "center",
          }}
        />
        {hasGames && (
          <Stack
            gap={SPACING}
            style={{
              margin: "0 auto",
              maxWidth: MAX_CONTENT_WIDTH,
              width: `calc(100vw - ${SPACING} * 2)`,
            }}
          >
            <Grid gridItemWidth={"300px"}>
              {games.map((game) => (
                <GameEntry key={game.id} game={game} />
              ))}
            </Grid>
          </Stack>
        )}
        {showBaseBlankState && <GamesBlankMessage />}
        {showFilteredEmptyState && (
          <Stack
            gap={SPACING}
            style={{
              alignItems: "center",
              textAlign: "center",
              padding: `${SPACING} 0`,
              fontFamily: FONT_FAMILY_GRAPHIK,
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>No results found.</p>
            <p style={{ margin: 0 }}>
              We couldn&apos;t find any records. Please refine the search criteria and try again.
            </p>
          </Stack>
        )}
        {!showFilteredEmptyState && (
          <FetchMoreButton dataMeta={dataGamesMeta} rows={games} rowsCount={dataGames?.currentUser?.gamesCount} />
        )}
      </PageContentStack>
    </>
  );
}
